import React from 'react';
import { Table, Button } from 'react-bootstrap';
import { formatPrice } from '../../../../shared/utils/formatPrice';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { SalesReportResponseV4 } from '../../../../services/cm-group-report/response/SalesReportResponseV4';
import { IconArrowUp, IconArrowDown } from '@tabler/icons-react';

interface SalesReportTableV4Props {
    data: SalesReportResponseV4;
    orderBy: string;
    sort: 'asc' | 'desc';
    setOrderBy: (field: string) => void;
    setSort: (direction: 'asc' | 'desc') => void;
}

const SalesReportTableV4: React.FC<SalesReportTableV4Props> = ({ data, orderBy, sort, setOrderBy, setSort }) => {
    const columns = [
        { label: "Property", key: "property_name" },
        { label: "Booking Date", key: "booking_date" },
        { label: "Source", key: "source" },
        { label: "Reservation Number", key: "reservation_number" },
        { label: "Guest Name", key: "guest_name" },
        { label: "Check-in", key: "checkin_date" },
        { label: "Check-out", key: "checkout_date" },
        { label: "Extra (IDR)", key: "extra" },
        { label: "Amount (IDR)", key: "amount" },
    ];

    const handleSort = (key: string) => {
        if (orderBy === key) {
            setSort(sort === 'asc' ? 'desc' : 'asc');
        } else {
            setOrderBy(key);
            setSort('asc');
        }
    };

    const renderSortIcon = (key: string) => {
        if (orderBy !== key) return null;
        return sort === 'asc' ? <IconArrowUp size={14} /> : <IconArrowDown size={14} />;
    };

    const formatRowValue = (row: any, colKey: string) => {
        const value = row[colKey as keyof typeof row];
        if (['extra', 'amount'].includes(colKey)) {
            return formatPrice(Number(value) || 0);
        }
        return value;
    };

    const exportToPDF = () => {
        const doc = new jsPDF({ orientation: 'landscape' });
        doc.text("Sales Report V4", 14, 10);
        const head = [columns.map(col => col.label)];
        const body = data.data.map(row => columns.map(col => formatRowValue(row, col.key)));
        const foot = [[
            "Total", "", "", "", "", "", "",
            formatPrice(Number(data.total.extra) || 0),
            formatPrice(Number(data.total.amount) || 0)
        ]];

        autoTable(doc, { head, body, foot });
        doc.save("SalesReportV4.pdf");
    };

    const exportToExcel = () => {
        const header = columns.map(col => col.label);
        const body = data.data.map(row => columns.map(col => formatRowValue(row, col.key)));
        const footer = [
            "Total", "", "", "", "", "", "",
            formatPrice(Number(data.total.extra) || 0),
            formatPrice(Number(data.total.amount) || 0)
        ];
        const wsData = [header, ...body, footer];

        const ws = XLSX.utils.aoa_to_sheet(wsData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Sales Report V4");
        const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
        saveAs(new Blob([excelBuffer], { type: "application/octet-stream" }), "SalesReportV4.xlsx");
    };

    return (
        <div className="mt-4">
            <div className="mb-3 d-flex gap-2">
                <Button variant="primary" onClick={exportToPDF}>Export to PDF</Button>
                <Button variant="success" onClick={exportToExcel}>Export to Excel</Button>
            </div>
            <div className="table-responsive">
                <Table striped bordered hover>
                    <thead>
                        <tr>
                            {columns.map((col) => (
                                <th key={col.key} onClick={() => handleSort(col.key)} style={{ cursor: 'pointer' }}>
                                    {col.label} {renderSortIcon(col.key)}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {data?.data?.length > 0 ? (
                            data.data.map((row, rowIndex) => (
                                <tr key={rowIndex}>
                                    {columns.map((col) => (
                                        <td key={col.key}>{formatRowValue(row, col.key)}</td>
                                    ))}
                                </tr>
                            ))
                        ) : (
                            <tr><td colSpan={columns.length} className="text-center">No data available</td></tr>
                        )}
                    </tbody>
                    <tfoot>
                        <tr>
                            <td colSpan={7}><strong>Total</strong></td>
                            <td><strong>{formatPrice(Number(data.total.extra) || 0)}</strong></td>
                            <td><strong>{formatPrice(Number(data.total.amount) || 0)}</strong></td>
                        </tr>
                    </tfoot>
                </Table>
            </div>
        </div>
    );
};

export default SalesReportTableV4;